import { MessageCircle, ThumbsUp, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	useInteracaoMetrics,
	useInteracaoPersonDrilldown,
} from '@/api/queries/kpi-metrics-queries'
import { ResponsiveFormModal } from '@/components/commons'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type {
	InteracaoPersonDrillRow,
	InteracaoPersonRole,
	InteracaoTableRow,
	InteracaoTopEmployeeEntry,
	InteracaoTopLeaderEntry,
	KpiMetricsParams,
} from '@/types'
import { AccordionBarChart } from './shared/accordion-bar-chart'
import { KpiMetricsDashboardLoading } from './shared/kpi-metrics-dashboard-loading'
import { KpiStatCard } from './shared/kpi-stat-card'
import {
	ResponsiveTable,
	type ResponsiveTableColumn,
} from './shared/responsive-table'

function ActionPill({ action }: { action: string }) {
	const { t } = useTranslation()

	if (action === 'ORIENTED') {
		return (
			<span className="inline-flex items-center rounded-full bg-kaefer-red/20 px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap text-kaefer-red">
				{t('kpiMetrics.interacao.oriented')}
			</span>
		)
	}

	if (action === 'CONGRATULATED') {
		return (
			<span className="inline-flex items-center rounded-full bg-kaefer-grass-green/15 px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap text-kaefer-forest-green">
				{t('kpiMetrics.interacao.congratulated')}
			</span>
		)
	}

	return <span className="text-xs text-muted-foreground">{action}</span>
}

function buildTableColumns(
	t: (key: string) => string,
): ResponsiveTableColumn<InteracaoTableRow>[] {
	return [
		{
			key: 'crName',
			header: t('kpiMetrics.interacao.tableCr'),
			render: (row) => row.crName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'date',
			header: t('kpiMetrics.interacao.tableDate'),
			render: (row) =>
				new Date(row.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
			className: 'whitespace-nowrap',
		},
		{
			key: 'employeeName',
			header: t('kpiMetrics.interacao.tableEmployee'),
			render: (row) => row.employeeName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'leaderName',
			header: t('kpiMetrics.interacao.tableLeader'),
			render: (row) => row.leaderName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'action',
			header: t('kpiMetrics.interacao.tableAction'),
			render: (row) => <ActionPill action={row.action} />,
		},
	]
}

export function InteracaoDashboard({ params }: { params: KpiMetricsParams }) {
	const { t } = useTranslation()
	const { data: interacao, isLoading } = useInteracaoMetrics(params)
	const [selectedRow, setSelectedRow] = useState<InteracaoTableRow | null>(null)
	const [selectedPerson, setSelectedPerson] = useState<{
		personId: string
		role: InteracaoPersonRole
		name: string
		jobRole?: string
	} | null>(null)

	if (isLoading || !interacao) return <KpiMetricsDashboardLoading />

	const tableColumns = buildTableColumns(t)

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
				<KpiStatCard
					icon={<MessageCircle className="size-4" />}
					iconClass="bg-kaefer-violet/15 text-kaefer-violet"
					label={t('kpiMetrics.interacao.total')}
					value={String(interacao.kpis.total)}
				/>
				<KpiStatCardWithPct
					icon={<TriangleAlert className="size-4" />}
					iconClass="bg-kaefer-red/20 text-kaefer-red"
					label={t('kpiMetrics.interacao.employeesOriented')}
					value={interacao.kpis.oriented}
					percentage={interacao.kpis.orientedPercentage}
					pillClass="bg-kaefer-red/20 text-kaefer-red"
				/>
				<KpiStatCardWithPct
					icon={<ThumbsUp className="size-4" />}
					iconClass="bg-kaefer-grass-green/15 text-kaefer-forest-green"
					label={t('kpiMetrics.interacao.employeesCongratulated')}
					value={interacao.kpis.congratulated}
					percentage={interacao.kpis.congratulatedPercentage}
					pillClass="bg-kaefer-grass-green/15 text-kaefer-forest-green"
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.interacao.byClassification')}
					</span>
					<BarChartLegend t={t} />
					<div className="mt-1 max-h-80 overflow-y-auto pr-1">
						<AccordionBarChart
							items={interacao.byClassification.map((item) => ({
								id: item.classificationId,
								label: item.classificationName,
								value: item.oriented + item.congratulated,
								children: item.subclassifications,
							}))}
							getChildId={(sub) => sub.subclassificationId}
							getChildLabel={(sub) => sub.subclassificationName}
							getChildValue={(sub) => sub.oriented + sub.congratulated}
							emptyState={<EmptyState t={t} />}
							renderRow={(item, maxParent) => (
								<OrientedCongratulatedBarRow
									label={item.label}
									oriented={
										interacao.byClassification.find(
											(c) => c.classificationId === item.id,
										)?.oriented ?? 0
									}
									congratulated={
										interacao.byClassification.find(
											(c) => c.classificationId === item.id,
										)?.congratulated ?? 0
									}
									max={maxParent}
									t={t}
								/>
							)}
							renderChildren={(item) => {
								const children = item.children ?? []
								const maxChild = Math.max(
									1,
									...children.map((sub) => sub.oriented + sub.congratulated),
								)
								return (
									<div className="flex flex-col gap-2">
										{children.map((sub) => (
											<OrientedCongratulatedBarRow
												key={sub.subclassificationId}
												label={sub.subclassificationName}
												oriented={sub.oriented}
												congratulated={sub.congratulated}
												max={maxChild}
												t={t}
												muted
											/>
										))}
									</div>
								)
							}}
						/>
					</div>
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.interacao.byBranch')}
					</span>
					<BarChartLegend t={t} />
					<div className="mt-1 max-h-80 overflow-y-auto pr-1">
						<AccordionBarChart
							items={interacao.byBranch.map((item) => ({
								id: item.branchId,
								label: item.branchName,
								value: item.oriented + item.congratulated,
								children: item.crList,
							}))}
							getChildId={(cr) => cr.crId}
							getChildLabel={(cr) => cr.crName}
							getChildValue={(cr) => cr.oriented + cr.congratulated}
							emptyState={<EmptyState t={t} />}
							renderRow={(item, maxParent) => (
								<OrientedCongratulatedBarRow
									label={item.label}
									oriented={
										interacao.byBranch.find((b) => b.branchId === item.id)
											?.oriented ?? 0
									}
									congratulated={
										interacao.byBranch.find((b) => b.branchId === item.id)
											?.congratulated ?? 0
									}
									max={maxParent}
									t={t}
								/>
							)}
							renderChildren={(item) => {
								const children = item.children ?? []
								const maxChild = Math.max(
									1,
									...children.map((cr) => cr.oriented + cr.congratulated),
								)
								return (
									<div className="flex flex-col gap-2">
										{children.map((cr) => (
											<OrientedCongratulatedBarRow
												key={cr.crId}
												label={cr.crName}
												oriented={cr.oriented}
												congratulated={cr.congratulated}
												max={maxChild}
												t={t}
												muted
											/>
										))}
									</div>
								)
							}}
						/>
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.interacao.rankingEmployees')}
					</span>
					<p className="text-xs text-muted-foreground">
						{t('kpiMetrics.interacao.rankingEmployeesSubtitle')}
					</p>
					<Tabs defaultValue="oriented-employees" className="mt-2">
						<TabsList className="flex w-full">
							<TabsTrigger value="oriented-employees" className="flex-1">
								{t('kpiMetrics.interacao.oriented')}
							</TabsTrigger>
							<TabsTrigger value="congratulated-employees" className="flex-1">
								{t('kpiMetrics.interacao.congratulated')}
							</TabsTrigger>
						</TabsList>
						<TabsContent value="oriented-employees">
							<Top10Table
								t={t}
								rows={interacao.topOrientedEmployees}
								onSelect={(row) =>
									setSelectedPerson({
										personId: row.employeeId,
										role: 'employee',
										name: row.employeeName,
										jobRole: row.jobRole,
									})
								}
							/>
						</TabsContent>
						<TabsContent value="congratulated-employees">
							<Top10Table
								t={t}
								rows={interacao.topCongratulatedEmployees}
								onSelect={(row) =>
									setSelectedPerson({
										personId: row.employeeId,
										role: 'employee',
										name: row.employeeName,
										jobRole: row.jobRole,
									})
								}
							/>
						</TabsContent>
					</Tabs>
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.interacao.rankingLeaders')}
					</span>
					<p className="text-xs text-muted-foreground">
						{t('kpiMetrics.interacao.rankingLeadersSubtitle')}
					</p>
					<Tabs defaultValue="oriented-leaders" className="mt-2">
						<TabsList className="flex w-full">
							<TabsTrigger value="oriented-leaders" className="flex-1">
								{t('kpiMetrics.interacao.oriented')}
							</TabsTrigger>
							<TabsTrigger value="congratulated-leaders" className="flex-1">
								{t('kpiMetrics.interacao.congratulated')}
							</TabsTrigger>
						</TabsList>
						<TabsContent value="oriented-leaders">
							<Top10Table
								t={t}
								rows={interacao.topOrientedLeaders}
								onSelect={(row) =>
									setSelectedPerson({
										personId: row.leaderId,
										role: 'leader',
										name: row.leaderName,
										jobRole: row.jobRole,
									})
								}
							/>
						</TabsContent>
						<TabsContent value="congratulated-leaders">
							<Top10Table
								t={t}
								rows={interacao.topCongratulatedLeaders}
								onSelect={(row) =>
									setSelectedPerson({
										personId: row.leaderId,
										role: 'leader',
										name: row.leaderName,
										jobRole: row.jobRole,
									})
								}
							/>
						</TabsContent>
					</Tabs>
				</div>
			</div>

			<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{t('kpiMetrics.interacao.table')}
				</span>
				{interacao.table.length === 0 ? (
					<EmptyState t={t} />
				) : (
					<div className="mt-2 max-h-96 overflow-auto">
						<ResponsiveTable
							columns={tableColumns}
							rows={interacao.table}
							rowKey={(row) => row.id}
							cardTitleKey="employeeName"
							cardBadge={(row) => <ActionPill action={row.action} />}
							onRowClick={(row) => setSelectedRow(row)}
						/>
					</div>
				)}
			</div>

			<ResponsiveFormModal
				open={Boolean(selectedRow)}
				onOpenChange={(open) => !open && setSelectedRow(null)}
				title={t('kpiMetrics.interacao.detailTitle')}
				dialogClassName="sm:max-w-lg"
			>
				{selectedRow && <InteracaoRowDetail t={t} row={selectedRow} />}
			</ResponsiveFormModal>

			<ResponsiveFormModal
				open={Boolean(selectedPerson)}
				onOpenChange={(open) => !open && setSelectedPerson(null)}
				title={selectedPerson?.name ?? ''}
				dialogClassName="sm:max-w-2xl"
			>
				{selectedPerson && (
					<InteracaoPersonDrilldown
						key={`${selectedPerson.role}-${selectedPerson.personId}`}
						t={t}
						params={params}
						personId={selectedPerson.personId}
						role={selectedPerson.role}
						jobRole={selectedPerson.jobRole}
					/>
				)}
			</ResponsiveFormModal>
		</div>
	)
}

function KpiStatCardWithPct({
	icon,
	iconClass,
	label,
	value,
	percentage,
	pillClass,
}: {
	icon: React.ReactNode
	iconClass: string
	label: string
	value: number
	percentage: number
	pillClass: string
}) {
	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			<div
				className={cn(
					'flex size-8 items-center justify-center rounded-md',
					iconClass,
				)}
			>
				{icon}
			</div>
			<div className="mt-2 flex items-center gap-2">
				<p className="text-2xl font-bold text-kaefer-dark-grey">{value}</p>
				<span
					className={cn(
						'rounded-full px-2 py-0.5 text-sm font-bold tabular-nums',
						pillClass,
					)}
				>
					{Math.round(percentage)}%
				</span>
			</div>
			<p className="text-xs font-medium text-kaefer-dark-grey">{label}</p>
		</div>
	)
}

function InteracaoRowDetail({
	t,
	row,
}: {
	t: (key: string) => string
	row: InteracaoTableRow
}) {
	const fields: { label: string; value: React.ReactNode }[] = [
		{ label: t('kpiMetrics.interacao.tableCr'), value: row.crName },
		{
			label: t('kpiMetrics.interacao.tableDate'),
			value: new Date(row.date).toLocaleDateString('pt-BR', {
				timeZone: 'UTC',
			}),
		},
		{ label: t('kpiMetrics.interacao.tableEmployee'), value: row.employeeName },
		{ label: t('kpiMetrics.interacao.tableLeader'), value: row.leaderName },
		{
			label: t('kpiMetrics.interacao.tableAction'),
			value: <ActionPill action={row.action} />,
		},
		{
			label: t('kpiMetrics.interacao.tableClassification'),
			value: row.classificationName,
		},
		{
			label: t('kpiMetrics.interacao.tableSubclassification'),
			value: row.subclassificationName,
		},
		{ label: t('kpiMetrics.interacao.tablePotential'), value: row.potential },
		{
			label: t('kpiMetrics.interacao.tableDescription'),
			value: row.description,
		},
	]

	return (
		<dl className="flex flex-col gap-3">
			{fields.map((field) => (
				<div key={field.label} className="flex flex-col gap-0.5">
					<dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
						{field.label}
					</dt>
					<dd className="text-sm text-kaefer-dark-grey">{field.value}</dd>
				</div>
			))}
		</dl>
	)
}

function OrientedCongratulatedBarRow({
	label,
	oriented,
	congratulated,
	max,
	t,
	muted,
}: {
	label: string
	oriented: number
	congratulated: number
	max: number
	t: (key: string) => string
	muted?: boolean
}) {
	const total = oriented + congratulated
	const widthPct = Math.max((total / max) * 100, total > 0 ? 2 : 0)
	const orientedPct = total > 0 ? (oriented / total) * 100 : 0
	const congratulatedPct = total > 0 ? (congratulated / total) * 100 : 0

	return (
		<div className="grid grid-cols-[1fr_2fr_auto] items-center gap-2.5">
			<span
				className={cn(
					'truncate text-xs',
					muted ? 'text-muted-foreground' : 'text-kaefer-dark-grey',
				)}
				title={label}
			>
				{label}
			</span>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="h-3.5 overflow-hidden bg-kaefer-light-silver/60">
						<div className="flex h-full" style={{ width: `${widthPct}%` }}>
							<div
								className="h-full bg-kaefer-red"
								style={{ width: `${orientedPct}%` }}
							/>
							<div
								className="h-full bg-kaefer-grass-green"
								style={{ width: `${congratulatedPct}%` }}
							/>
						</div>
					</div>
				</TooltipTrigger>
				<TooltipContent side="top" align="center" className="max-w-64">
					<div className="flex flex-col gap-1">
						<div className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5">
							<span className="size-2 shrink-0 rounded-[2px] bg-kaefer-red" />
							<span className="truncate text-left">
								{t('kpiMetrics.interacao.oriented')}
							</span>
							<span className="text-muted-foreground tabular-nums">
								{oriented} ({Math.round(orientedPct)}%)
							</span>
						</div>
						<div className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5">
							<span className="size-2 shrink-0 rounded-[2px] bg-kaefer-grass-green" />
							<span className="truncate text-left">
								{t('kpiMetrics.interacao.congratulated')}
							</span>
							<span className="text-muted-foreground tabular-nums">
								{congratulated} ({Math.round(congratulatedPct)}%)
							</span>
						</div>
					</div>
				</TooltipContent>
			</Tooltip>
			<span className="text-right text-xs font-semibold text-muted-foreground tabular-nums">
				{total}
			</span>
		</div>
	)
}

function BarChartLegend({ t }: { t: (key: string) => string }) {
	return (
		<div className="mt-2 flex gap-4 text-[11px] text-muted-foreground">
			<span className="flex items-center gap-1.5">
				<span className="size-2.5 rounded-[2px] bg-kaefer-red" />
				{t('kpiMetrics.interacao.oriented')}
			</span>
			<span className="flex items-center gap-1.5">
				<span className="size-2.5 rounded-[2px] bg-kaefer-grass-green" />
				{t('kpiMetrics.interacao.congratulated')}
			</span>
		</div>
	)
}

function Top10Table<
	TRow extends InteracaoTopEmployeeEntry | InteracaoTopLeaderEntry,
>({
	t,
	rows,
	onSelect,
}: {
	t: (key: string) => string
	rows: TRow[]
	onSelect?: (row: TRow) => void
}) {
	if (rows.length === 0) return <EmptyState t={t} />

	const top10 = rows.slice(0, 10)

	return (
		<div className="mt-1 flex max-h-80 flex-col gap-1 overflow-auto pr-1">
			{top10.map((row, index) => (
				<button
					key={
						'employeeId' in row ? row.employeeId : `${row.leaderId}-${index}`
					}
					type="button"
					onClick={() => onSelect?.(row)}
					className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-md px-2 py-1.5 text-left odd:bg-kaefer-light-silver/30 hover:bg-kaefer-light-silver/60"
				>
					<span
						className={cn(
							'flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
							index < 3
								? 'bg-kaefer-sapphire-blue text-white'
								: 'bg-kaefer-light-silver/60 text-kaefer-dark-grey',
						)}
					>
						{index + 1}
					</span>
					<span className="flex min-w-0 flex-col">
						<span className="truncate text-sm text-kaefer-dark-grey">
							{'employeeId' in row ? row.employeeName : row.leaderName}
						</span>
						{row.jobRole && (
							<span className="truncate text-xs text-muted-foreground">
								{row.jobRole}
							</span>
						)}
					</span>
					<span className="text-right text-xs font-semibold text-muted-foreground tabular-nums">
						{row.count}
					</span>
				</button>
			))}
		</div>
	)
}

interface ActionTally {
	id: string
	label: string
	oriented: number
	congratulated: number
}

type DrillFilter = 'ALL' | 'ORIENTED' | 'CONGRATULATED'

function emptyTally(id: string, label: string): ActionTally {
	return { id, label, oriented: 0, congratulated: 0 }
}

function addAction(entry: ActionTally, action: string) {
	if (action === 'ORIENTED') entry.oriented += 1
	else if (action === 'CONGRATULATED') entry.congratulated += 1
}

function byTotalDesc(a: ActionTally, b: ActionTally) {
	return b.oriented + b.congratulated - (a.oriented + a.congratulated)
}

function groupByCategory(rows: InteracaoPersonDrillRow[]) {
	const categories = new Map<
		string,
		{ tally: ActionTally; subs: Map<string, ActionTally> }
	>()

	for (const row of rows) {
		const label = row.classificationName || '—'
		let category = categories.get(label)
		if (!category) {
			category = { tally: emptyTally(label, label), subs: new Map() }
			categories.set(label, category)
		}
		addAction(category.tally, row.action)

		const subLabel = row.subclassificationName || '—'
		let sub = category.subs.get(subLabel)
		if (!sub) {
			sub = emptyTally(`${label}::${subLabel}`, subLabel)
			category.subs.set(subLabel, sub)
		}
		addAction(sub, row.action)
	}

	return [...categories.values()]
		.map(({ tally, subs }) => ({
			...tally,
			children: [...subs.values()].sort(byTotalDesc),
		}))
		.sort(byTotalDesc)
}

function groupByCounterpart(
	rows: InteracaoPersonDrillRow[],
	role: InteracaoPersonRole,
) {
	const people = new Map<string, ActionTally>()

	for (const row of rows) {
		const name = (role === 'leader' ? row.employeeName : row.leaderName) || '—'
		let person = people.get(name)
		if (!person) {
			person = emptyTally(name, name)
			people.set(name, person)
		}
		addAction(person, row.action)
	}

	return [...people.values()].sort(byTotalDesc)
}

function InteracaoPersonDrilldown({
	t,
	params,
	personId,
	role,
	jobRole,
}: {
	t: (key: string) => string
	params: KpiMetricsParams
	personId: string
	role: InteracaoPersonRole
	jobRole?: string
}) {
	const { data, isLoading } = useInteracaoPersonDrilldown(
		personId,
		role,
		params,
	)
	const [filter, setFilter] = useState<DrillFilter>('ALL')

	if (isLoading || !data) return <PersonDrilldownLoading />

	const { total, oriented, congratulated } = data.kpis
	const orientedPct = total > 0 ? (oriented / total) * 100 : 0
	const congratulatedPct = total > 0 ? (congratulated / total) * 100 : 0

	const categories = groupByCategory(data.table)
	const counterparts = groupByCounterpart(data.table, role)
	const topCategory = categories[0]
	const maxCategory = Math.max(
		1,
		...categories.map((c) => c.oriented + c.congratulated),
	)
	const maxCounterpart = Math.max(
		1,
		...counterparts.map((p) => p.oriented + p.congratulated),
	)
	const records = [...data.table]
		.filter((row) => filter === 'ALL' || row.action === filter)
		.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

	const counterpartLabel =
		role === 'leader'
			? t('kpiMetrics.interacao.drillTabEmployees')
			: t('kpiMetrics.interacao.drillTabLeaders')

	return (
		<div className="flex min-w-0 flex-col gap-4">
			<div className="-mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
				<span className="rounded-full bg-kaefer-sapphire-blue/10 px-2 py-0.5 font-semibold text-kaefer-sapphire-blue">
					{role === 'leader'
						? t('kpiMetrics.interacao.tableLeader')
						: t('kpiMetrics.interacao.tableEmployee')}
				</span>
				{jobRole && <span className="truncate">{jobRole}</span>}
			</div>

			<p className="text-xs text-muted-foreground">
				{role === 'leader'
					? t('kpiMetrics.interacao.drillLeaderSubtitle')
					: t('kpiMetrics.interacao.drillEmployeeSubtitle')}
			</p>

			{data.table.length === 0 ? (
				<EmptyState t={t} />
			) : (
				<>
					<div className="rounded-lg border border-kaefer-light-silver p-3">
						<div className="grid grid-cols-3 gap-3">
							<DrillStat
								label={t('kpiMetrics.interacao.total')}
								value={total}
							/>
							<DrillStat
								label={t('kpiMetrics.interacao.oriented')}
								value={oriented}
								percentage={orientedPct}
								dotClass="bg-kaefer-red"
							/>
							<DrillStat
								label={t('kpiMetrics.interacao.congratulated')}
								value={congratulated}
								percentage={congratulatedPct}
								dotClass="bg-kaefer-grass-green"
							/>
						</div>
						<div className="mt-3 flex h-2 overflow-hidden rounded-full bg-kaefer-light-silver/60">
							<div
								className="h-full bg-kaefer-red"
								style={{ width: `${orientedPct}%` }}
							/>
							<div
								className="h-full bg-kaefer-grass-green"
								style={{ width: `${congratulatedPct}%` }}
							/>
						</div>
						{topCategory && (
							<p className="mt-3 truncate text-xs text-muted-foreground">
								{t('kpiMetrics.interacao.drillTopCategory')}:{' '}
								<span className="font-semibold text-kaefer-dark-grey">
									{topCategory.label}
								</span>{' '}
								<span className="tabular-nums">
									({topCategory.oriented + topCategory.congratulated})
								</span>
							</p>
						)}
					</div>

					<Tabs defaultValue="categories">
						<TabsList className="flex w-full">
							<TabsTrigger value="categories" className="flex-1">
								{t('kpiMetrics.interacao.drillTabCategories')}
							</TabsTrigger>
							<TabsTrigger value="people" className="flex-1">
								{counterpartLabel}
							</TabsTrigger>
							<TabsTrigger value="records" className="flex-1">
								{t('kpiMetrics.interacao.drillTabRecords')}
								<span className="ml-1 text-muted-foreground tabular-nums">
									{data.table.length}
								</span>
							</TabsTrigger>
						</TabsList>

						<TabsContent value="categories">
							<BarChartLegend t={t} />
							<div className="max-h-[45vh] overflow-y-auto pr-1">
								<AccordionBarChart
									items={categories.map((category) => ({
										...category,
										value: category.oriented + category.congratulated,
									}))}
									getChildId={(sub) => sub.id}
									getChildLabel={(sub) => sub.label}
									getChildValue={(sub) => sub.oriented + sub.congratulated}
									renderRow={(item) => (
										<OrientedCongratulatedBarRow
											label={item.label}
											oriented={item.oriented}
											congratulated={item.congratulated}
											max={maxCategory}
											t={t}
										/>
									)}
									renderChildren={(item) => {
										const maxChild = Math.max(
											1,
											...item.children.map(
												(sub) => sub.oriented + sub.congratulated,
											),
										)
										return (
											<div className="flex flex-col gap-2">
												{item.children.map((sub) => (
													<OrientedCongratulatedBarRow
														key={sub.id}
														label={sub.label}
														oriented={sub.oriented}
														congratulated={sub.congratulated}
														max={maxChild}
														t={t}
														muted
													/>
												))}
											</div>
										)
									}}
								/>
							</div>
						</TabsContent>

						<TabsContent value="people">
							<BarChartLegend t={t} />
							<div className="mt-3 flex max-h-[45vh] flex-col gap-2.5 overflow-y-auto pr-1">
								{counterparts.map((person) => (
									<OrientedCongratulatedBarRow
										key={person.id}
										label={person.label}
										oriented={person.oriented}
										congratulated={person.congratulated}
										max={maxCounterpart}
										t={t}
									/>
								))}
							</div>
						</TabsContent>

						<TabsContent value="records">
							<div className="mt-1 flex gap-1.5">
								{(['ALL', 'ORIENTED', 'CONGRATULATED'] as const).map(
									(option) => (
										<button
											key={option}
											type="button"
											onClick={() => setFilter(option)}
											className={cn(
												'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
												filter === option
													? 'border-kaefer-dark-grey bg-kaefer-dark-grey text-white'
													: 'border-kaefer-light-silver text-kaefer-dark-grey hover:bg-kaefer-light-silver/40',
											)}
										>
											{option === 'ALL'
												? t('kpiMetrics.interacao.drillAll')
												: option === 'ORIENTED'
													? t('kpiMetrics.interacao.oriented')
													: t('kpiMetrics.interacao.congratulated')}
										</button>
									),
								)}
							</div>
							{records.length === 0 ? (
								<EmptyState t={t} />
							) : (
								<ul className="mt-3 flex max-h-[45vh] flex-col gap-2 overflow-y-auto pr-1">
									{records.map((row) => (
										<DrillRecord key={row.id} t={t} row={row} role={role} />
									))}
								</ul>
							)}
						</TabsContent>
					</Tabs>
				</>
			)}
		</div>
	)
}

function DrillStat({
	label,
	value,
	percentage,
	dotClass,
}: {
	label: string
	value: number
	percentage?: number
	dotClass?: string
}) {
	return (
		<div className="flex min-w-0 flex-col gap-0.5">
			<span className="flex items-center gap-1.5 truncate text-[11px] font-medium text-muted-foreground">
				{dotClass && (
					<span className={cn('size-2 shrink-0 rounded-[2px]', dotClass)} />
				)}
				{label}
			</span>
			<span className="flex items-baseline gap-1.5">
				<span className="text-xl font-bold text-kaefer-dark-grey tabular-nums">
					{value}
				</span>
				{percentage !== undefined && (
					<span className="text-xs font-semibold text-muted-foreground tabular-nums">
						{Math.round(percentage)}%
					</span>
				)}
			</span>
		</div>
	)
}

function DrillRecord({
	t,
	row,
	role,
}: {
	t: (key: string) => string
	row: InteracaoPersonDrillRow
	role: InteracaoPersonRole
}) {
	const counterpart = role === 'leader' ? row.employeeName : row.leaderName
	const counterpartLabel =
		role === 'leader'
			? t('kpiMetrics.interacao.tableEmployee')
			: t('kpiMetrics.interacao.tableLeader')

	return (
		<li className="rounded-md border border-kaefer-light-silver p-3">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 flex-col">
					<span className="truncate text-sm font-semibold text-kaefer-dark-grey">
						{row.classificationName}
						{row.subclassificationName && (
							<span className="font-normal text-muted-foreground">
								{' '}
								· {row.subclassificationName}
							</span>
						)}
					</span>
					<span className="truncate text-xs text-muted-foreground">
						{new Date(row.date).toLocaleDateString('pt-BR', {
							timeZone: 'UTC',
						})}
						{counterpart && ` · ${counterpartLabel}: ${counterpart}`}
					</span>
				</div>
				<ActionPill action={row.action} />
			</div>
			{row.description && (
				<p className="mt-2 line-clamp-3 text-sm whitespace-pre-line text-kaefer-dark-grey">
					{row.description}
				</p>
			)}
		</li>
	)
}

function PersonDrilldownLoading() {
	return (
		<div className="flex flex-col gap-4">
			<Skeleton className="h-5 w-40" />
			<Skeleton className="h-24 w-full" />
			<Skeleton className="h-9 w-full" />
			<div className="flex flex-col gap-2">
				<Skeleton className="h-4 w-full" />
				<Skeleton className="h-4 w-5/6" />
				<Skeleton className="h-4 w-4/6" />
			</div>
		</div>
	)
}

function EmptyState({ t }: { t: (key: string) => string }) {
	return (
		<p className="py-10 text-center text-sm text-muted-foreground">
			{t('kpiMetrics.charts.noData')}
		</p>
	)
}
